/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { ReactElement, useEffect, useRef } from 'react';
import { getHighlightParts } from '@/ux-common/src/utils/getHighlightParts';

type SplitViewPanelProps = {
  payload: {
    longPrompt: string;
    role: ReactElement;
  };
  searchTerm: string;
  currentMatchIndex: number;
  onMatchesChange: (els: HTMLElement[]) => void;
};

export const SplitViewPanel: React.FC<SplitViewPanelProps> = ({
  payload,
  searchTerm,
  currentMatchIndex,
  onMatchesChange,
}) => {
  const { longPrompt, role } = payload;

  const refs = useRef<HTMLElement[]>([]);

  //  Reset Refs before we map over.
  refs.current = [];

  const typeSafeLongPrompt = Array.isArray(longPrompt)
    ? (longPrompt[0].content as string)
    : longPrompt;
  // const parts = regex ? typeSafeLongPrompt.split(regex) : [typeSafeLongPrompt];
  const parts = getHighlightParts(typeSafeLongPrompt, searchTerm);

  // After each render, notify parent of the collected match refs:
  // Spread into a new array so parent’s setMatches actually sees a change
  useEffect(() => {
    onMatchesChange([...refs.current]);
  }, [typeSafeLongPrompt, searchTerm]);

  // Scroll the active match into view:
  useEffect(() => {
    refs.current[currentMatchIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [currentMatchIndex, refs.current]);

  // now render, counting matches with a local counter
  let matchCounter = 0;

  return (
    <div className="h-full px-4 pb-1 pt-[10px]">
      {role}
      <p className="whitespace-pre-wrap pt-1 text-white">
        {parts.map((part, index) => {
          if (part.isMatch) {
            const idx = matchCounter++;
            return (
              <span
                key={index}
                ref={(el) => {
                  if (el) refs.current[idx] = el;
                }}
                className={`rounded px-px ${
                  idx === currentMatchIndex
                    ? 'bg-mustard-600' // Current match style
                    : 'bg-mustard-300 text-black' // Other matches style
                }`}
              >
                {part.text}
              </span>
            );
          }
          // If it's not a match, we render it as plain text.
          return <span key={index}>{part.text}</span>;
        })}
      </p>
    </div>
  );
};
